import { Link, useNavigate, useLocation } from "react-router-dom";
import { Home, Search, Plus, LayoutDashboard, LogIn, LogOut, User, KeyRound, UserPlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavGuard } from "../context/NavGuardContext";
import { ConnectionBanner } from "./ConnectionBanner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_reencuentro-mvp/artifacts/qsbn2fc7_-Version%20lineal.png";

export function Layout({ children }) {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { attempt } = useNavGuard();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const goToSection = (id) => {
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scroll, 350);
    } else {
      scroll();
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F5F6F8]">
      <ConnectionBanner />
      <header className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB] no-print">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/" onClick={(e) => { e.preventDefault(); attempt(() => navigate("/")); }} className="flex items-center shrink-0" data-testid="header-logo-link">
            <img src={LOGO_URL} alt="Reencuentro" className="h-8 sm:h-9 w-auto" />
          </Link>

          <nav className="flex items-center gap-1">
            <Link to="/buscar" data-testid="nav-search-link"
              className="hidden md:flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#003893] transition-colors">
              <Search className="w-4 h-4" /> Buscar persona
            </Link>
            <Link to="/consultar-reporte" data-testid="nav-lookup-link"
              className="hidden lg:flex items-center px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#003893] transition-colors">
              Consultar reporte
            </Link>
            <button onClick={() => attempt(() => goToSection("home-how-it-works"))} data-testid="nav-howitworks-link"
              className="hidden lg:flex items-center px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#003893] transition-colors">
              Cómo funciona
            </button>
            <button onClick={() => attempt(() => goToSection("home-about"))} data-testid="nav-about-link"
              className="hidden lg:flex items-center px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#003893] transition-colors">
              Nosotros
            </button>
            <button onClick={() => attempt(() => navigate("/contacto"))} data-testid="nav-contact-link"
              className="hidden lg:flex items-center px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#003893] transition-colors">
              Contáctanos
            </button>

            <span className="hidden md:block w-px h-6 bg-[#E5E7EB] mx-1" />

            {isAdmin && (
              <Link to="/admin" data-testid="nav-admin-link"
                className="flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-semibold bg-[#003893] text-white hover:bg-[#002a6e] transition-colors">
                <LayoutDashboard className="w-4 h-4" /> <span className="hidden sm:inline">Panel Admin</span>
              </Link>
            )}
            {user ? (
              <>
                <Link to="/mis-reportes" data-testid="nav-myreports-link"
                  className="flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9]">
                  <User className="w-4 h-4" /> <span className="hidden sm:inline">Mis reportes</span>
                </Link>
                <Link to="/cambiar-contrasena" data-testid="nav-changepw-link" title="Cambiar contraseña"
                  className="flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9]">
                  <KeyRound className="w-4 h-4" /> <span className="hidden sm:inline">Contraseña</span>
                </Link>
                <button onClick={() => attempt(handleLogout)} data-testid="nav-logout-btn"
                  className="flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9]">
                  <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Salir</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link"
                  className="flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium text-[#475569] hover:bg-[#F1F5F9] hover:text-[#003893] transition-colors">
                  <LogIn className="w-4 h-4" /> <span className="hidden sm:inline">Entrar</span>
                </Link>
                <Link to="/registro" data-testid="nav-register-link"
                  className="flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-semibold bg-[#003893] text-white hover:bg-[#002a6e] transition-colors shadow-sm">
                  <UserPlus className="w-4 h-4" /> Registrarse
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 py-6 pb-24 sm:pb-10 fade-in">
        {children}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E5E7EB] sm:hidden no-print">
        <div className="grid grid-cols-3 h-16">
          <button onClick={() => attempt(() => navigate("/"))} data-testid="bottom-home"
            className="flex flex-col items-center justify-center gap-1 text-[#475569] active:bg-[#F1F5F9]">
            <Home className="w-5 h-5" strokeWidth={2} /><span className="text-xs font-medium">Inicio</span>
          </button>
          <BottomLink to="/buscar" icon={Search} label="Buscar" testid="bottom-search" />
          <BottomLink to="/reportar/desaparecida" icon={Plus} label="Reportar" testid="bottom-report" />
        </div>
      </nav>
    </div>
  );
}

function BottomLink({ to, icon: Icon, label, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="flex flex-col items-center justify-center gap-1 text-[#475569] active:bg-[#F1F5F9]">
      <Icon className="w-5 h-5" strokeWidth={2} />
      <span className="text-xs font-medium">{label}</span>
    </Link>
  );
}
